import { ArrowLeft, Mail, Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { brandConfig } from "../../config/brand";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Section } from "../components/ui/Section";
import { getAnalyticsSessionId } from "../lib/analyticsClient";

async function readApiMessage(response: Response) {
  const data = await response.json().catch(() => null);
  if (data && typeof data === "object" && "message" in data) return String(data.message);
  const error = data && typeof data === "object" && "error" in data ? data.error : null;
  if (error && typeof error === "object" && "message" in error) return String(error.message);
  return response.ok ? "Το μήνυμά σου καταχωρήθηκε." : "Δεν μπορέσαμε να καταχωρήσουμε το μήνυμα αυτή τη στιγμή.";
}

export function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const submitContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, email, subject, message, analyticsSessionId: getAnalyticsSessionId() })
      });
      const nextFeedback = await readApiMessage(response);

      if (!response.ok) {
        setStatus("error");
        setFeedback(nextFeedback);
        return;
      }

      setStatus("success");
      setFeedback(nextFeedback);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch {
      setStatus("error");
      setFeedback("Δεν μπορέσαμε να καταχωρήσουμε το μήνυμα αυτή τη στιγμή.");
    }
  };

  return (
    <Section className="py-12 sm:py-16">
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
        <div>
          <Mail className="text-cyan-200" size={32} />
          <h1 className="mt-4 text-3xl font-bold text-white sm:text-4xl">Επικοινωνία</h1>
          <p className="mt-3 text-slate-300">
            Για υποστήριξη σχετικά με το test, το report ή την πρόσβαση σε αποτέλεσμα, μπορείς να στείλεις μήνυμα στην ομάδα TryBrainTest.
          </p>
          <p className="mt-5 rounded-lg border border-white/10 bg-white/[0.045] p-4 text-sm leading-6 text-slate-300">
            Εναλλακτικά, μπορείς να μας γράψεις στο{" "}
            <a className="font-semibold text-cyan-100 hover:text-white" href={`mailto:${brandConfig.supportEmail}`}>
              {brandConfig.supportEmail}
            </a>
            .
          </p>
        </div>
        <Card>
          <p className="text-sm font-semibold text-cyan-100">Υποστήριξη TryBrainTest</p>
          <form className="mt-5 space-y-4" onSubmit={submitContact}>
            <div>
              <label className="text-sm font-semibold text-slate-200" htmlFor="contact-name">
                Όνομα προαιρετικά
              </label>
              <Input id="contact-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={80} className="mt-2" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-200" htmlFor="contact-email">
                Email
              </label>
              <Input id="contact-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} maxLength={160} className="mt-2" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-200" htmlFor="contact-subject">
                Θέμα προαιρετικά
              </label>
              <Input id="contact-subject" value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={120} className="mt-2" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-200" htmlFor="contact-message">
                Μήνυμα
              </label>
              <textarea
                id="contact-message"
                required
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={2000}
                rows={6}
                className="mt-2 w-full rounded-lg border border-slate-500/30 bg-slate-950/40 px-4 py-3 text-sm text-white placeholder:text-slate-500"
              />
            </div>
            {feedback ? (
              <p className={`rounded-lg border p-3 text-sm ${status === "success" ? "border-emerald-200/25 bg-emerald-200/10 text-emerald-100" : "border-rose-200/25 bg-rose-200/10 text-rose-100"}`}>
                {feedback}
              </p>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button type="submit" disabled={status === "submitting"} className="w-full sm:w-auto">
                <Send className="mr-2" size={18} /> {status === "submitting" ? "Αποστολή..." : "Αποστολή μηνύματος"}
              </Button>
              <Button href="/" variant="secondary" className="w-full sm:w-auto">
                <ArrowLeft className="mr-2" size={18} /> Επιστροφή στην αρχική
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Section>
  );
}
